import React, { useMemo } from 'react';
import { Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import {
  Initiative,
  MonthlyPerformance,
  StrategyAttribution,
} from '@totem/components/services/optimize/v2/dashboard/types';

type AttributionRow = {
  key: string;
  initiativeName: string;
  strategy: string;
  chargeType: string;
  totalSavings: number;
};

type Props = {
  initiatives: Initiative[];
  performanceMonths: MonthlyPerformance[];
};

const formatCurrency = (val: number) =>
  `$${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STRATEGY_LABELS: Record<string, string> = {
  staggered_startup: 'Staggered Startup',
  demand_limiting: 'Demand Limiting',
  optimal_start_stop: 'Optimal Start/Stop',
  schedule_optimization: 'Schedule Optimization',
  after_hours_management: 'After-Hours Management',
};

const InitiativeSavingsTable = ({ initiatives, performanceMonths }: Props) => {
  const rows = useMemo<AttributionRow[]>(() => {
    // Aggregate savings from all monthly attribution records by strategy+chargeType
    const totals = new Map<string, number>();

    performanceMonths.forEach((month) => {
      (month.strategyAttribution ?? []).forEach((attr: StrategyAttribution) => {
        const key = `${attr.strategy}__${attr.chargeType}`;
        totals.set(key, (totals.get(key) ?? 0) + attr.savingsDollars);
      });
    });

    // Map each initiative's strategy to its accumulated savings
    return initiatives
      .filter((ini) => ini.status !== 'removed')
      .map((ini) => {
        const energyKey = `${ini.strategy}__energy`;
        const demandKey = `${ini.strategy}__demand`;
        const totalSavings =
          (totals.get(energyKey) ?? 0) + (totals.get(demandKey) ?? 0);

        return {
          key: ini.id,
          initiativeName: ini.name,
          strategy: ini.strategy,
          chargeType: ini.chargeType,
          totalSavings,
        };
      })
      .sort((aRow, bRow) => bRow.totalSavings - aRow.totalSavings);
  }, [initiatives, performanceMonths]);

  const columns: ColumnsType<AttributionRow> = [
    {
      title: 'Initiative',
      dataIndex: 'initiativeName',
      key: 'initiativeName',
    },
    {
      title: 'Strategy',
      dataIndex: 'strategy',
      key: 'strategy',
      render: (val: string) => STRATEGY_LABELS[val] ?? val,
    },
    {
      title: 'Charge Type',
      dataIndex: 'chargeType',
      key: 'chargeType',
      render: (val: string) => (
        <Tag color={val === 'energy' ? 'blue' : 'orange'}>
          {val.charAt(0).toUpperCase() + val.slice(1)}
        </Tag>
      ),
    },
    {
      title: 'Total Savings',
      dataIndex: 'totalSavings',
      key: 'totalSavings',
      align: 'right',
      render: (val: number) => formatCurrency(val),
      sorter: (aRow, bRow) => aRow.totalSavings - bRow.totalSavings,
    },
  ];

  return (
    <Table<AttributionRow>
      columns={columns}
      dataSource={rows}
      pagination={false}
      size="small"
      rowKey="key"
      summary={(pageData) => {
        const total = pageData.reduce((sum, row) => sum + row.totalSavings, 0);
        return (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={3}>
              <strong>Total</strong>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={1} align="right">
              <strong>{formatCurrency(total)}</strong>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        );
      }}
    />
  );
};

export default InitiativeSavingsTable;
